/**
 * Dirty timetable / route-details için lazy indeks.
 * Full rebuild Web Worker'da; upsertStopTime incremental patch main'de.
 */

let epochSeen = -1
let tripsByRoute = null
let stopTimesByTrip = null
let stopTimeByTripStop = null
let stopsById = null
let shapesById = null
let stopIdsByRoute = null

let worker = null
let workerEpochQueued = -1
let rebuildPromise = null
let rebuildResolve = null
const readyListeners = new Set()

function currentEpoch() {
  return globalThis.__geFeedIndexEpoch || 0
}

function objectToMap(obj) {
  const m = new Map()
  if (!obj) return m
  for (const key of Object.keys(obj)) {
    m.set(key, obj[key])
  }
  return m
}

function applyWorkerPayload(payload) {
  tripsByRoute = objectToMap(payload.tripsByRoute)
  stopTimesByTrip = objectToMap(payload.stopTimesByTrip)
  stopTimeByTripStop = objectToMap(payload.stopTimeByTripStop)
  stopsById = objectToMap(payload.stopsById)
  shapesById = objectToMap(payload.shapesById)
  stopIdsByRoute = objectToMap(payload.stopIdsByRoute)
  epochSeen = payload.epoch
}

function rebuildSync(getRows) {
  tripsByRoute = new Map()
  for (const t of getRows('trips') || []) {
    const rid = String(t.route_id ?? '')
    if (!tripsByRoute.has(rid)) tripsByRoute.set(rid, [])
    tripsByRoute.get(rid).push(t)
  }

  stopTimesByTrip = new Map()
  stopTimeByTripStop = new Map()
  for (const st of getRows('stop_times') || []) {
    const tid = String(st.trip_id ?? '')
    const sid = String(st.stop_id ?? '')
    if (!stopTimesByTrip.has(tid)) stopTimesByTrip.set(tid, [])
    stopTimesByTrip.get(tid).push(st)
    stopTimeByTripStop.set(`${tid}||${sid}`, st)
  }

  stopsById = new Map()
  for (const s of getRows('stops') || []) {
    stopsById.set(String(s.stop_id), s)
  }

  shapesById = new Map()
  for (const p of getRows('shapes') || []) {
    const sid = String(p.shape_id ?? '')
    if (!shapesById.has(sid)) shapesById.set(sid, [])
    shapesById.get(sid).push(p)
  }

  stopIdsByRoute = new Map()
  for (const [rid, routeTrips] of tripsByRoute) {
    const seenDir = new Set()
    const stopIds = new Set()
    for (const trip of routeTrips) {
      const did = String(trip.direction_id ?? '0')
      if (seenDir.has(did)) continue
      seenDir.add(did)
      const times = stopTimesByTrip.get(String(trip.trip_id)) || []
      for (const st of times) stopIds.add(String(st.stop_id))
      if (seenDir.size >= 2) break
    }
    if (!stopIds.size) {
      for (const trip of routeTrips) {
        const times = stopTimesByTrip.get(String(trip.trip_id)) || []
        for (const st of times) stopIds.add(String(st.stop_id))
      }
    }
    stopIdsByRoute.set(rid, stopIds)
  }

  epochSeen = currentEpoch()
}

function ensureWorker() {
  if (worker || typeof Worker === 'undefined') return worker
  try {
    worker = new Worker(
      new URL('../../workers/gtfsFeedIndex.worker.js', import.meta.url)
    )
    worker.onmessage = (event) => {
      const msg = event.data || {}
      if (msg.type === 'ready') {
        if (msg.epoch !== currentEpoch()) return
        applyWorkerPayload(msg)
        // stopIdsByRoute worker'dan Array gelir → Set'e çevir
        if (stopIdsByRoute) {
          const next = new Map()
          for (const [rid, ids] of stopIdsByRoute) {
            next.set(rid, ids instanceof Set ? ids : new Set(ids || []))
          }
          stopIdsByRoute = next
        }
        if (rebuildResolve) {
          rebuildResolve(true)
          rebuildResolve = null
          rebuildPromise = null
        }
        readyListeners.forEach((fn) => {
          try {
            fn(epochSeen)
          } catch {
            /* ignore */
          }
        })
      } else if (msg.type === 'error') {
        console.warn('GTFS index worker hata:', msg.error)
        if (rebuildResolve) {
          rebuildResolve(false)
          rebuildResolve = null
          rebuildPromise = null
        }
      }
    }
    worker.onerror = (err) => {
      console.warn('GTFS index worker error:', err)
    }
  } catch (e) {
    console.warn('GTFS index worker başlatılamadı, sync rebuild:', e)
    worker = null
  }
  return worker
}

/**
 * Store mutasyonu sonrası worker rebuild planla.
 * @returns {Promise<boolean>}
 */
export function scheduleFeedIndexRebuild(getRows) {
  const epoch = currentEpoch()
  if (epochSeen === epoch && tripsByRoute) {
    return Promise.resolve(true)
  }

  const w = ensureWorker()
  if (!w) {
    rebuildSync(getRows)
    readyListeners.forEach((fn) => {
      try {
        fn(epochSeen)
      } catch {
        /* ignore */
      }
    })
    return Promise.resolve(true)
  }

  if (rebuildPromise && workerEpochQueued === epoch) {
    return rebuildPromise
  }

  workerEpochQueued = epoch
  rebuildPromise = new Promise((resolve) => {
    rebuildResolve = resolve
  })

  w.postMessage({
    type: 'rebuild',
    epoch,
    trips: getRows('trips') || [],
    stop_times: getRows('stop_times') || [],
    stops: getRows('stops') || [],
    shapes: getRows('shapes') || [],
  })

  return rebuildPromise
}

/** İndeksler hazır olana kadar bekle (timeout ile sync fallback). */
export async function ensureIndexesReady(getRows, { timeoutMs = 8000 } = {}) {
  if (epochSeen === currentEpoch() && tripsByRoute) return true

  const pending = scheduleFeedIndexRebuild(getRows)
  let timer
  const timed = new Promise((resolve) => {
    timer = setTimeout(() => resolve('timeout'), timeoutMs)
  })
  const result = await Promise.race([pending.then(() => 'ok'), timed])
  clearTimeout(timer)

  if (epochSeen === currentEpoch() && tripsByRoute) return true
  if (result === 'timeout' || result === 'ok') {
    // Worker geciktiyse veya başarısızsa sync
    if (epochSeen !== currentEpoch() || !tripsByRoute) {
      rebuildSync(getRows)
    }
  }
  return true
}

export function onFeedIndexesReady(fn) {
  readyListeners.add(fn)
  return () => readyListeners.delete(fn)
}

export function areFeedIndexesReady() {
  return epochSeen === currentEpoch() && !!tripsByRoute
}

function ensure(getRows) {
  if (
    !tripsByRoute ||
    !stopTimesByTrip ||
    !stopTimeByTripStop ||
    !stopsById ||
    !shapesById ||
    epochSeen !== currentEpoch()
  ) {
    rebuildSync(getRows)
  }
}

export function invalidateGtfsFeedIndexes() {
  epochSeen = -1
  tripsByRoute = null
  stopTimesByTrip = null
  stopTimeByTripStop = null
  stopsById = null
  shapesById = null
  stopIdsByRoute = null
  globalThis.__geFeedIndexEpoch = (globalThis.__geFeedIndexEpoch || 0) + 1
  workerEpochQueued = -1
}

export function getTripsForRoute(getRows, routeId) {
  ensure(getRows)
  return tripsByRoute.get(String(routeId)) || []
}

export function getStopTimesForTripIds(getRows, tripIds) {
  ensure(getRows)
  const out = []
  for (const id of tripIds) {
    const rows = stopTimesByTrip.get(String(id))
    if (rows?.length) out.push(...rows)
  }
  return out
}

export function findStopTimeRow(getRows, tripId, stopId) {
  ensure(getRows)
  return stopTimeByTripStop.get(`${String(tripId)}||${String(stopId)}`) || null
}

/** Hücre upsert sonrası indeks güncelle — full rebuild yok */
export function patchStopTimeIndex(tripId, stopId, row, isNew) {
  if (!stopTimeByTripStop || !stopTimesByTrip) return
  const tid = String(tripId)
  const sid = String(stopId)
  const key = `${tid}||${sid}`
  const prev = stopTimeByTripStop.get(key)
  stopTimeByTripStop.set(key, row)
  if (isNew || !prev) {
    if (!stopTimesByTrip.has(tid)) stopTimesByTrip.set(tid, [])
    const arr = stopTimesByTrip.get(tid)
    if (!arr.includes(row)) arr.push(row)
  } else if (prev !== row) {
    const arr = stopTimesByTrip.get(tid)
    if (arr) {
      const i = arr.indexOf(prev)
      if (i >= 0) arr[i] = row
    }
  }
  // route stop cache invalidate for affected trip's route — cheap clear
  stopIdsByRoute = null
}

export function getStopIdsForRoute(getRows, routeId) {
  ensure(getRows)
  const rid = String(routeId)
  if (stopIdsByRoute?.has(rid)) {
    return stopIdsByRoute.get(rid)
  }
  const trips = getTripsForRoute(getRows, routeId)
  const seenDir = new Set()
  const stopIds = new Set()
  for (const trip of trips) {
    const did = String(trip.direction_id ?? '0')
    if (seenDir.has(did)) continue
    seenDir.add(did)
    const times = stopTimesByTrip.get(String(trip.trip_id)) || []
    for (const st of times) stopIds.add(String(st.stop_id))
    if (seenDir.size >= 2) break
  }
  if (!stopIds.size) {
    const tripIds = trips.map((t) => String(t.trip_id))
    const stopTimes = getStopTimesForTripIds(getRows, tripIds)
    for (const st of stopTimes) stopIds.add(String(st.stop_id))
  }
  if (!stopIdsByRoute) stopIdsByRoute = new Map()
  stopIdsByRoute.set(rid, stopIds)
  return stopIds
}

export function getStopById(getRows, stopId) {
  ensure(getRows)
  return stopsById.get(String(stopId)) || null
}

export function getStopsMap(getRows) {
  ensure(getRows)
  return stopsById
}

export function getShapePoints(getRows, shapeId) {
  ensure(getRows)
  return shapesById.get(String(shapeId)) || []
}

export function getShapePointCount(getRows, shapeId) {
  ensure(getRows)
  return (shapesById.get(String(shapeId)) || []).length
}
